import React from 'react';
import { Route, Navigate } from 'react-router-dom';
import { lazyWithRetry } from '../utils/lazyWithRetry';
import RequireRole from '../layouts/RequireRole';
import RequireActiveStatus from '../layouts/RequireActiveStatus';
import type { AuthUser } from '../context/AuthContextCore';

const DashboardSwitcher = lazyWithRetry(() => import('../layouts/components/DashboardSwitcher'));
const UserProfile = lazyWithRetry(() => import('../features/user/UserProfile'));
const NotificationsPage = lazyWithRetry(() => import('../features/user/NotificationsPage'));
const OperationsPage = lazyWithRetry(() => import('../features/operations/OperationsPage'));
const Academy = lazyWithRetry(() => import('../features/academy/Academy'));
const RidersView = lazyWithRetry(() => import('../features/fleet/RidersView'));
const SupportHub = lazyWithRetry(() => import('../features/franchise/SupportHub'));
const ResourcesPanel = lazyWithRetry(() => import('../features/franchise/ResourcesPanel'));
const DevSandbox = lazyWithRetry(() => import('../pages/DevSandbox'));
const FlyderFlotaView = lazyWithRetry(() => import('../features/franchise/flyder/FlyderFlotaView'));

interface SharedRoutesProps {
    user: AuthUser | null;
    /**
     * Flota cuyos riders se muestran. `undefined` para un admin sin flota
     * seleccionada, que ve el listado completo.
     */
    franchiseId?: string;
}

export function SharedRoutes({ user, franchiseId }: SharedRoutesProps) {
    return (
        <>
            <Route path="notifications" element={
                <React.Suspense fallback={<div className="p-10 text-center">Cargando Notificaciones...</div>}>
                    <NotificationsPage />
                </React.Suspense>
            } />
            <Route index element={
                ['rider'].includes(user?.role || '')
                    ? <Navigate to="/rider/dashboard" replace />
                    : (user?.role === 'admin' || user?.role === 'franchise'
                        ? <Navigate to="/dashboard" replace />
                        : <Navigate to="/profile" replace />)
            } />
            <Route path="dashboard" element={
                <RequireRole allowedRoles={['admin', 'franchise']}>
                    <RequireActiveStatus>
                        <DashboardSwitcher />
                    </RequireActiveStatus>
                </RequireRole>
            } />
            <Route path="operations" element={
                <RequireRole allowedRoles={['admin', 'franchise']}>
                    <RequireActiveStatus>
                        <OperationsPage />
                    </RequireActiveStatus>
                </RequireRole>
            } />
            <Route path="academy" element={
                <RequireRole allowedRoles={['admin', 'franchise']}>
                    <Academy />
                </RequireRole>
            } />
            <Route path="academy/:moduleId" element={
                <RequireRole allowedRoles={['admin', 'franchise']}>
                    <Academy />
                </RequireRole>
            } />
            <Route path="fleet" element={
                <RequireRole allowedRoles={['admin', 'franchise']}>
                    <RequireActiveStatus>
                        <RidersView franchiseId={franchiseId} />
                    </RequireActiveStatus>
                </RequireRole>
            } />
            <Route path="flyder" element={
                <RequireRole allowedRoles={['admin', 'franchise']}>
                    <RequireActiveStatus>
                        <div className="p-4">
                            <FlyderFlotaView franchiseId={franchiseId} />
                        </div>
                    </RequireActiveStatus>
                </RequireRole>
            } />
            <Route path="invoicing" element={<Navigate to="/dashboard" replace />} />
            <Route path="profile" element={<UserProfile />} />
            <Route path="demo/smart-input" element={<DevSandbox />} />
            <Route path="support" element={
                <RequireRole allowedRoles={['admin', 'franchise']}>
                    <SupportHub />
                </RequireRole>
            } />
            <Route path="resources" element={
                <RequireRole allowedRoles={['admin', 'franchise']}>
                    <ResourcesPanel />
                </RequireRole>
            } />
        </>
    );
}
